<template>
    <div class="help" v-if="data">

        <!-- Tabs -->
        <div class="tabs">
            <div class="tabs__list" role="tablist" aria-label="Help Tabs">
                <div v-for="tabData, index in data" :key="'tab-' + index" class="tab-accordion-wrap">
                    <button class="tab" :class="{ 'tab--active': activeTab === index }" :id="'help-tab-' + index"
                        @click.prevent="selectTab(index)" role="tab" :aria-controls="'help-panel-' + index"
                        :aria-selected="activeTab === index ? true : false">
                        {{ tabData.tabName }}
                    </button>
                    <div class="accordion" :ref="el => { if (el) accordionRefs[index] = el }">
                        <div class="accordion__content">
                            <h3 v-if="tabData.heading" class="accordion__heading">{{ tabData.heading }}</h3>
                            <div v-if="tabData.textBody" class="accordion__text" v-html="tabData.textBody"></div>
                            <div class="accordion__button" v-html="tabData.link"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="tabs__clone">
                <div v-for="tabData, index in data" :key="'tab-clone-' + index" class="tab__active-clone"
                    :style="{ opacity: activeTab === index ? 1 : 0 }">
                    <div class="tab__active-bg">
                        <div class="tab__active-content">{{ tabData.tabName }}</div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Tabs End -->

        <!-- Tab Panels -->
        <div class="tab-panels">
            <div v-for="tabData, index in data" :key="'tabBody-' + index" v-show="activeTab === index" role="tabpanel"
                class="tab-panel" :id="'help-panel-' + index" :aria-labelledby="'help-tab-' + index"
                :hidden="activeTab !== index">
                <h3 v-if="tabData.heading" class="tab-panel__heading">{{ tabData.heading }}</h3>
                <div v-if="tabData.textBody" class="tab-panel__text" v-html="tabData.textBody"></div>
                <div class="tab-panel__button" v-html="tabData.link"></div>
            </div>
        </div>
        <!-- Tab Panels End -->

    </div>
</template>

<script setup>
import { onMounted, onBeforeUpdate, ref, watch } from 'vue';

defineProps({
    data: {
        type: [Array, Object],
        default: null
    }
});

const activeTab = ref(0);
watch(() => activeTab.value, toggleAccordion);

function selectTab(i) {
    activeTab.value = i
};

// Open the first accordion on page load
onMounted(toggleAccordion);

const accordionRefs = ref([]);
function toggleAccordion(open = 0, close) {
    // Show the active accordion
    const accordionToOpen = accordionRefs.value[open]
    if (accordionToOpen) {
        accordionToOpen.style.maxHeight = `${accordionToOpen.scrollHeight}px`
    }

    // Hide the old accordion
    if (typeof close === 'number') {
        const accordionToClose = accordionRefs.value[close]
        if (accordionToClose) {
            accordionToClose.style.maxHeight = null
        }
    }
};
// make sure to reset the refs before each update (From Vue documentation https://v3.vuejs.org/guide/composition-api-template-refs.html#usage-inside-v-for)
onBeforeUpdate(() => {
    accordionRefs.value = []
});
</script>

<style lang="scss" scoped>
.help {
    @apply flex flex-col items-start my-8 pb-2;

    @screen lg {
        @apply flex-row items-stretch mt-8 mb-0 pb-0;
    }
}

.tabs {
    @apply relative w-full mr-6;

    @screen lg {
        @apply w-5/12;
    }

    &__list {
        @apply flex flex-col rounded-xl shadow-v2 overflow-hidden w-full;
    }

    &__clone {
        @apply hidden;

        @screen lg {
            @apply block absolute w-full h-full top-0 left-0 pointer-events-none;
        }
    }
}

.tab {
    @apply text-xl text-grey-darkest bg-white py-3 px-3 text-left w-full h-full leading-none;

    &:focus,
    &:active {
        @apply outline-none;
    }

    &--active {
        @apply text-white bg-orange font-bold;

        @screen lg {
            @apply text-grey-darkest bg-white font-normal;
        }
    }

    &__active-clone {
        @supports (filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.16))) {
            filter: drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.16));
        }
    }

    &__active-bg {
        @apply text-xl text-orange border border-orange font-bold bg-orange text-left shadow-v2 leading-none;

        @supports (clip-path: polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0 100%)) {
            @apply shadow-none;
            clip-path: polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0 100%);
            width: calc(100% + 3.125rem);
        }
    }

    &__active-content {
        @apply bg-white py-3 px-3;

        @supports (clip-path: polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0 100%)) {
            clip-path: polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0 100%);
        }
    }
}

.tab-panels {
    @apply hidden;

    @screen lg {
        @apply block w-7/12 bg-white rounded-xl shadow-v2;
    }
}

.tab-panel {
    @apply p-8;

    &__heading {
        @apply text-[1.625rem] text-grey-darkest font-bold mb-4;
    }

    &__text {
        @apply text-lg text-grey-darkest mb-8;
    }

    &__button {
        @apply text-center;

        & :deep(a),
        button,
        .btn,
        .button {
            @apply inline-flex justify-center items-center text-2xl text-center rounded-sm max-w-full whitespace-normal;
            min-width: 50%;
        }
    }
}

.tab-accordion-wrap {
    @apply flex flex-col;

    &:not(:last-child) {
        @apply border-b-2 border-slate-200;
    }
}

.accordion {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.2s ease-out;

    @screen lg {
        @apply hidden;
    }

    &__content {
        @apply p-6 bg-white;
    }

    &__heading {
        @apply text-[1.625rem] text-grey-darkest font-bold mb-4;
    }

    &__text {
        @apply text-lg text-grey-darkest mb-8;
    }

    &__button {
        @apply text-center;

        & :deep(a),
        button,
        .btn,
        .button {
            @apply inline-flex justify-center items-center text-lg text-center rounded-sm max-w-full whitespace-normal;
            min-width: 50%;
        }
    }
}
</style>